import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { getLeadDetailsUrl } from "../../utils/urls";
import {
  EnvelopeIcon,
  PhoneIcon,
  CalendarIcon,
  MapPinIcon,
} from "@heroicons/react/24/outline";
import LeadEngagementPanel from "../../components/LeadEngagement/LeadEngagementPanel";

const LeadDetails = () => {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [leadData, setLeadData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!leadId) {
      setIsLoading(false);
      setError("No lead ID provided");
      return;
    }

    const fetchLeadDetails = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get(`${getLeadDetailsUrl}/${leadId}`);
        console.log("lead data", response.data.data);
        setLeadData(response.data.data);
      } catch (err) {
        setError(err.response?.data?.message || "Failed to fetch lead details");
      } finally {
        setIsLoading(false);
      }
    };

    fetchLeadDetails();
  }, [leadId]);

  // Map lead data to the UI structure using provided fields
  const displayData = {
    lprId: leadData?.lead_product_relation_display_id,
    name: leadData?.lead_id?.name || "Unknown",
    email: leadData?.lead_id?.email || "N/A",
    mobileNo: leadData?.lead_id?.contact_number || "N/A",
    dob: leadData?.lead_id?.dob || "12 Aug 1999",
    address: leadData?.address || "6301, Solitaire Phase 3 Boner, Pune",
    city: leadData?.city || "Pune",
    state: leadData?.state || "Maharashtra",
    priority: leadData?.priority || "N/A",
    relationshipManager:
      leadData?.lead_product_relationship_manager_relation
        ?.relationship_manager_id.name || "N/A",
    leadId: leadData?.lead_display_id || "N/A",
    lead_status: leadData?.lead_status_id?.title || "N/A",
  };

  // Static plans data to match Figma design
  const plansData = [
    {
      planId: leadData?.product_id?.product_id,
      planName: leadData?.product_id?.product_name || leadData?.sub_category_id?.title || leadData?.category_id?.title,
      company: leadData?.product_id?.company_id?.company_name || null,
      description: leadData?.product_id?.description || leadData?.sub_category_id?.description || leadData?.category_id?.description,
      financial_description: leadData?.product_id?.financial_description,
    },
  ];

  return (
    <div className="page-container">
      {/* Header */}
      <div className="flex justify-between items-center mb-6 border-b border-[#E2EBF3] pb-4">
        <h1 className="text-xl font-semibold text-blue-600">Lead Details</h1>
      </div>

      <div className="flex flex-row max-sm:flex-col gap-4">
        {/* Left Section: Lead Information */}
        <div className="flex-[0_0_calc(50%_-_0.5rem)] bg-white rounded-2xl shadow-sm p-6 border border-[#EBEBEF]">
          <div className="flex flex-row justify-between items-start gap-3 mb-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                <span className="text-gray-600 font-medium">
                  {displayData.name.charAt(0)}
                </span>
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-800">
                  {displayData.name}
                </h2>
                <p className="text-[#425F80] text-[0.65rem] font-[500]">
                  {displayData.lprId}
                </p>
              </div>
            </div>
            <div
              className={`py-1 px-3 rounded-full text-xs font-medium ${
                displayData?.lead_status === "Assigned"
                  ? "bg-orange-100 text-orange-800"
                  : "bg-gray-100 text-gray-800"
              }`}
            >
              {displayData?.lead_status}
            </div>
          </div>

          <div className="flex flex-col gap-2">
            {[
              {
                label: "Email",
                icon: <EnvelopeIcon className="size-5 text-[#2F4E74]" />,
                value: displayData.email,
              },
              {
                label: "Mobile No.",
                icon: <PhoneIcon className="size-5 text-[#2F4E74]" />,
                value: displayData.mobileNo,
              },
              {
                label: "Date of Birth",
                icon: <CalendarIcon className="size-5 text-[#2F4E74]" />,
                value: displayData.dob,
              },
              {
                label: "Address",
                icon: <MapPinIcon className="size-5 text-[#2F4E74]" />,
                value: displayData.address,
              },
              {
                label: "City",
                icon: <MapPinIcon className="size-5 text-[#2F4E74]" />,
                value: displayData.city,
              },
              {
                label: "State",
                icon: <MapPinIcon className="size-5 text-[#2F4E74]" />,
                value: displayData.state,
              },
            ].map((item, index) => (
              <div className="flex flex-row gap-4" key={index}>
                <div className="flex flex-row gap-2 min-w-[120px] max-sm:min-w-[100px]">
                  {item.icon}
                  <p className="text-[0.875rem] font-[500] text-[#2F4E74]">
                    {item.label}
                  </p>
                </div>
                <div>
                  <p className="text-[0.875rem] font-[500] text-[#213650]">
                    {item.value}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right Section: Plans */}
        {plansData.map((plan, index) => (
          <div className="flex-[0_0_calc(50%_-_0.5rem)] flex flex-col gap-4 bg-white rounded-2xl shadow-sm p-6 border border-[#EBEBEF]">
            <div key={index} className="bg-white flex flex-col relative">
              <div className="flex items-start">
                <div className="">
                  <h3 className="text-[0.875rem] font-[600] text-[#152B47] mb-2">
                    {plan.planName}
                  </h3>
                  {plan.company &&
                    <p className="text-[0.875rem] font-[500] text-[#152B47] mb-2">
                      {plan.company}
                    </p>
                  }

                  <div className="flex flex-col gap-1">
                    <h3 className="text-[0.62rem] font-[500] text-[#425F80]">
                      Description :
                    </h3>
                    <p className="text-[0.62rem] font-[600] text-[#213650]">
                      {plan.description}
                    </p>
                  </div>
                  { plan.financial_description &&
                    <div className="flex flex-col gap-1 mt-4">
                      <h3 className="text-[0.62rem] font-[500] text-[#425F80]">
                        Financial Description :
                      </h3>
                      <p className="text-[0.62rem] font-[600] text-[#213650]">
                        {plan.financial_description}
                      </p>
                    </div>
                  }
                </div>
              </div>

              {/* View Details button at the bottom-right */}
              {plan?.planId &&
                <div className="flex justify-end mt-4">
                  <button className="text-blue-600 hover:underline flex items-center gap-1.5 text-sm"
                    onClick={() => {
                      navigate("/products/details/"+plan?.planId)
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-4 h-4"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    View Details
                  </button>
                </div>
              }
            </div>
          </div>
        ))}
      </div>

      {leadId && (
        <div className="mt-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-3">Engagement</h2>
          <LeadEngagementPanel
            lprId={leadData?.lpr_id || leadId}
            mobile={displayData.mobileNo}
            currentStatusId={leadData?.lead_status_id?.id}
            name={displayData.name}
          />
        </div>
      )}
    </div>
  );
};

export default LeadDetails;
